import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'components/ui';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import { wrapFunctionalComponent } from 'utils/framework';
import { space, forms } from 'style/config';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import basketUtils from 'utils/Basket';
import deliveryFrequency from 'utils/DeliveryFrequency';
import { isChangeQuantityEnabled } from 'utils/arChangeQuantityFlag';
import languageLocale from 'utils/LanguageLocale';
import AutoReplenishDetails from 'components/ProductPage/FulfillmentMethods/AutoReplenishDetails';
import FulfillmentMethodsEN from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_en_US';
import FulfillmentMethodsFR from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_fr_CA';

const { formatSavingAmountString, formatCurrency } = deliveryFrequency;
const getText = languageLocale.getLocalisedDataFromResource(FulfillmentMethodsEN, FulfillmentMethodsFR);

const PER_UNIT_QUANTITY = 1;
// Indent the expanded details so they align under the row title (checkbox box + gap),
// matching the Figma layout where everything sits in the column beside the checkbox.
const DETAILS_INDENT = forms.RADIO_SIZE + space[2];

const styles = {
    checkbox: {
        '.Checkbox-box': { marginRight: space[2] }
    },
    // Reset the shared Checkbox baseline nudge (-.0625em) so the single-line ineligible
    // label centers with the disabled box (SHP-5407). The disabled slash, #ccc border and
    // #f6f6f8 fill come from the shared Checkbox disabled treatment.
    checkboxIneligible: {
        '.Checkbox-box': { marginTop: 0 }
    },
    // Mobile: title and price stack (space[1] / 2 apart). Desktop: title left, price right.
    titleRow: {
        columnGap: space[2],
        rowGap: space[1] / 2
    },
    price: {
        whiteSpace: 'nowrap'
    },
    details: {
        marginLeft: DETAILS_INDENT
    }
};

/**
 * AutoReplenishRow renders the "Save 5% with Auto-Replenish" checkbox beneath
 * the shipping details panel.
 *
 * SHP-5122 makes the checkbox interactive: it is a controlled input bound to the
 * shared `commerce` Auto-Replenish state (from `useCommerce`), so toggling it
 * reproduces the exact same update logic that clicking the legacy AR delivery
 * card does today — no backend or breaking changes, UI only. Because the state
 * is shared with the commerce/basket flow, enrolling here carries through to
 * add-to-basket just like the legacy layout.
 *
 * When checked and the current SKU is replenishment-eligible, the discounted
 * price appears on the title row (right-aligned on desktop, stacked below the
 * title on mobile) and the expanded `AutoReplenishDetails` (delivery frequency
 * selector, consent copy, "Learn more") renders below, collapsing on uncheck.
 * When the current SKU is not eligible the checkbox is disabled and its inline
 * label is replaced by the gray "Not eligible for Auto-Replenish" copy per Figma
 * (SHP-5139). When no SKU within the product is eligible the row is not rendered
 * at all (gated by `showAutoReplenishment` in the parent).
 *
 * @param {object} props
 * @param {object} props.currentSku - The current SKU (drives pricing/eligibility).
 * @param {object} props.commerce - Shared Auto-Replenish state from useCommerce.
 * @param {boolean} props.isReplenishmentEligible - Whether the current SKU is AR-eligible.
 * @returns {JSX.Element}
 */
const AutoReplenishRow = ({ currentSku, commerce = {}, isReplenishmentEligible }) => {
    const { autoReplenishChecked, autoReplenishClick, quantity } = commerce;

    const { acceleratedPromotion, replenishmentAdjuster } = currentSku || {};
    const listPrice = parseFloat(basketUtils.removeCurrency(currentSku?.listPrice));
    // Discounted (Auto-Replenish) price. Mirrors the field resolution in
    // `formatSavingAmountString` (replenishmentAdjusterPrice, falling back to discountedPrice).
    const adjusterPrice = parseFloat(basketUtils.removeCurrency(currentSku?.replenishmentAdjusterPrice || currentSku?.discountedPrice));
    const displayQty = isChangeQuantityEnabled() && autoReplenishChecked ? Number(quantity) || PER_UNIT_QUANTITY : PER_UNIT_QUANTITY;
    const showPrice = Boolean(autoReplenishChecked && replenishmentAdjuster);
    // The savings can be any percentage (5%, 10%, 15%, …) or a dollar amount depending
    // on the SKU's replenishment adjuster, so derive it instead of hardcoding "5%".
    // `displayDiscountOnly` returns just the discount value ("5%" / "$5.00").
    const savingsDiscount = replenishmentAdjuster ? formatSavingAmountString(currentSku, PER_UNIT_QUANTITY, true) : '';

    const fireAutoReplenishClickAnalytics = () => {
        digitalData.page.attributes.outOfStock = currentSku?.isOutOfStock ? `Yes - ${anaConsts.FULFILLMENT_TYPE.AutoReplenish}` : '';

        pdpAnalytics.trackLinkClick({
            actionInfo: anaConsts.EVENT_NAMES.AUTO_REPLENISH_RADIO_BUTTON_CLICK,
            prop44: digitalData.page.attributes.outOfStock,
            productStrings: `;${currentSku?.skuId};;;;eVar26=${currentSku?.skuId}`
        });
    };

    const handleChange = event => {
        const isChecked = event.target.checked;

        autoReplenishClick?.(isChecked);

        if (isChecked) {
            fireAutoReplenishClickAnalytics();
        }
    };

    return (
        <>
            <Checkbox
                name='fulfillmentMethodsAutoReplenish'
                checked={Boolean(autoReplenishChecked)}
                disabled={!isReplenishmentEligible}
                onChange={handleChange}
                // Ineligible: omit aria-label so Checkbox keeps the visible label announced; an
                // aria-label would make it aria-hide the "Not eligible…" text (SHP-5371).
                aria-label={isReplenishmentEligible ? getText('autoReplenishRowCheckboxAriaLabel', [savingsDiscount]) : undefined}
                width='100%'
                alignItems={isReplenishmentEligible ? 'flex-start' : 'center'}
                css={[styles.checkbox, !isReplenishmentEligible && styles.checkboxIneligible]}
            >
                {isReplenishmentEligible ? (
                    <Flex
                        width='100%'
                        flexDirection={['column', 'row']}
                        justifyContent={['flex-start', 'space-between']}
                        alignItems={['flex-start', 'baseline']}
                        css={styles.titleRow}
                    >
                        {savingsDiscount && (
                            <Box>
                                <Text
                                    is='span'
                                    fontSize='sm'
                                    fontWeight='bold'
                                    color='red'
                                    children={getText('autoReplenishRowSavePercent', [savingsDiscount])}
                                />
                                <Text
                                    is='span'
                                    fontSize='sm'
                                    fontWeight='bold'
                                    color='black'
                                    children={getText('autoReplenishRowSuffix')}
                                />
                            </Box>
                        )}
                        {showPrice && (
                            <Box css={styles.price}>
                                <Text
                                    is='span'
                                    fontSize='sm'
                                    fontWeight='bold'
                                    color='red'
                                    children={`${formatCurrency(adjusterPrice, displayQty)}${acceleratedPromotion ? '*' : ''} `}
                                />
                                <Text
                                    is='del'
                                    fontSize='sm'
                                    color='gray'
                                    children={formatCurrency(listPrice, displayQty)}
                                />
                            </Box>
                        )}
                    </Flex>
                ) : (
                    <Text
                        is='span'
                        fontSize='sm'
                        color='gray'
                        children={getText('autoReplenishRowNotEligible')}
                    />
                )}
            </Checkbox>
            {isReplenishmentEligible && autoReplenishChecked && (
                <Box css={styles.details}>
                    <AutoReplenishDetails
                        currentSku={currentSku}
                        commerce={commerce}
                    />
                </Box>
            )}
        </>
    );
};

AutoReplenishRow.propTypes = {
    currentSku: PropTypes.object,
    commerce: PropTypes.object,
    isReplenishmentEligible: PropTypes.bool
};

export default wrapFunctionalComponent(AutoReplenishRow, 'AutoReplenishRow');
